// File: /var/www/app2/frontend/src/components/UserManagement.tsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { User } from "../App";

// Interface for form data
interface UserFormData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: ControlRole;
  homeCountryId: string;
  active: boolean;
}

// Multi-country roles (control DB). Organizations are retired in favour of country.
type ControlRole = "supporter" | "country_manager" | "system_admin" | "field_user";
const ADMIN_ROLES = ["admin", "superadmin", "system_admin", "country_manager"];
const isAdminRole = (r?: string): boolean => !!r && ADMIN_ROLES.includes(r);

// Interface for backend user data (control DB shape)
interface BackendUser {
  id: number;
  username: string;
  full_name: string | null;
  email: string;
  role: ControlRole;
  home_country_id: number | null;
  active: boolean;
}

// Interface for country option data
interface Country {
  id: number;
  name: string;
  code?: string;
}

interface UserManagementProps {
  user: User;
}

function UserManagement({ user }: UserManagementProps): React.ReactElement {
  const [users, setUsers] = useState<BackendUser[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesError, setCountriesError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<BackendUser | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Form data
  const [formData, setFormData] = useState<UserFormData>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "supporter",
    homeCountryId: "",
    active: true,
  });

  useEffect(() => {
    if (isAdminRole(user.role)) {
      fetchUsers();
      fetchCountries();
    }
  }, [user]);

  const fetchUsers = async (): Promise<void> => {
    try {
      const response = await axios.get<{ users: BackendUser[] }>("/api/users");
      setUsers(response.data.users || []);
      setError("");
    } catch (error: any) {
      console.error("Failed to fetch users:", error);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async (): Promise<void> => {
    try {
      const response = await axios.get<any>("/api/countries");
      const d = response.data?.data || response.data;
      setCountries(d.countries || []);
      setCountriesError(false);
    } catch (error: any) {
      console.error("Failed to fetch countries:", error);
      setCountriesError(true);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    const target = e.target as HTMLInputElement & HTMLSelectElement;
    const { name, value, type, checked } = target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const newRole = e.target.value as ControlRole;
    setFormData({ ...formData, role: newRole });
  };

  const resetForm = (): void => {
    setFormData({
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "supporter",
      homeCountryId: "",
      active: true,
    });
    setEditingUser(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!editingUser && formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!editingUser && formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    // Single-country roles need a country.
    if ((formData.role === "supporter" || formData.role === "field_user") && !formData.homeCountryId) {
      setError("Country is required for supporter and field-user roles");
      return;
    }

    // Confirm any change to Role before saving. Deliberately not tied to
    // *how* the value changed (typed, clicked, or an accidental mouse-wheel
    // scroll over the focused <select> silently changing it -- a real,
    // confirmed browser quirk, see Iris/Playwright findings 2026-08-12) --
    // any save that would change a user's role gets one explicit check,
    // since a silent role change is a real privilege-escalation risk either way.
    if (editingUser && formData.role !== editingUser.role) {
      const roleLabel = (r: string) =>
        ({ supporter: "Supporter", field_user: "Field User", country_manager: "Country Manager", system_admin: "System Admin" }[r] || r);
      const confirmed = window.confirm(
        `Change ${editingUser.username}'s role from "${roleLabel(editingUser.role)}" to "${roleLabel(formData.role)}"?`
      );
      if (!confirmed) return;
    }

    // Same scroll-jack exposure applies to the Country <select> -- confirm
    // any change there too before saving, for the same reason as Role above.
    if (
      editingUser &&
      formData.homeCountryId !== (editingUser.home_country_id ? String(editingUser.home_country_id) : "")
    ) {
      const countryLabel = (id: string) => (id ? countries.find((c) => String(c.id) === id)?.name || "(unknown)" : "(none)");
      const confirmed = window.confirm(
        `Change ${editingUser.username}'s country from "${countryLabel(editingUser.home_country_id ? String(editingUser.home_country_id) : "")}" to "${countryLabel(formData.homeCountryId)}"?`
      );
      if (!confirmed) return;
    }

    try {
      const userData: Record<string, any> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        email: formData.email,
        role: formData.role,
        homeCountryId: formData.homeCountryId ? parseInt(formData.homeCountryId, 10) : null,
        active: formData.active,
      };

      if (!editingUser) {
        // Create new user
        userData.password = formData.password;
        await axios.post("/api/users", userData);
        setSuccess("User created successfully!");
      } else {
        // Update existing user
        if (formData.password) {
          userData.password = formData.password;
        }
        await axios.put(`/api/users/${editingUser.id}`, userData);
        setSuccess("User updated successfully!");
      }

      resetForm();
      fetchUsers();
    } catch (error: any) {
      // Show detailed validation errors if available
      const details = error.response?.data?.details;
      if (details && Array.isArray(details) && details.length > 0) {
        const messages = details.map((d: any) => d.msg).join("; ");
        setError(messages);
      } else {
        setError(error.response?.data?.error || "Failed to save user");
      }
    }
  };

  const handleEdit = (userToEdit: BackendUser): void => {
    // full_name is one column (see BackendUser) — this form still splits it into
    // two boxes for editing, so split on the first space rather than dumping the
    // whole name into firstName and leaving lastName blank (which silently
    // duplicated the name if lastName was then edited: saving composed
    // "firstName + ' ' + lastName" from the backend's deriveFullName).
    const nameParts = (userToEdit.full_name || "").trim().split(/\s+/);
    setFormData({
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(1).join(" "),
      username: userToEdit.username,
      email: userToEdit.email || "",
      password: "",
      confirmPassword: "",
      role: userToEdit.role,
      homeCountryId: userToEdit.home_country_id ? String(userToEdit.home_country_id) : "",
      active: userToEdit.active,
    });
    setEditingUser(userToEdit);
    setShowAddForm(true);
    setError("");
    setSuccess("");
  };

  const handleToggleActive = async (
    userId: number,
    currentStatus: boolean
  ): Promise<void> => {
    try {
      await axios.patch(`/api/users/${userId}/toggle-active`, {
        active: !currentStatus,
      });
      fetchUsers();
      setSuccess("User status updated");
    } catch (error: any) {
      setError("Failed to update user status");
    }
  };

  const handleDelete = async (userId: number, username: string): Promise<void> => {
    if (!window.confirm(`Are you sure you want to delete user ${username}?`)) {
      return;
    }

    try {
      await axios.delete(`/api/users/${userId}`);
      setSuccess("User deleted successfully");
      fetchUsers();
    } catch (error: any) {
      setError("Failed to delete user");
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(
    (u) =>
      u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdminRole(user.role)) {
    return (
      <div style={{ padding: "40px", color: "white", textAlign: "center" }}>
        <h2>Access Denied</h2>
        <p>Only administrators can manage users.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "40px 20px" }}>
      <h2 style={{ color: "white", marginBottom: "30px" }}>User Management</h2>

      {/* Add/Edit User Form */}
      {showAddForm && (
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "25px",
            marginBottom: "30px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h3 style={{ marginBottom: "20px", color: "#2d3748" }}>
            {editingUser ? "Edit User" : "Add New User"}
          </h3>

          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Username *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  disabled={editingUser ? true : false}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: editingUser ? "#f7fafc" : "white",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Password {!editingUser && "*"}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  placeholder={
                    editingUser ? "Leave blank to keep current password" : ""
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Confirm Password {!editingUser && "*"}
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required={!editingUser}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleRoleChange}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                >
                  <option value="supporter">Supporter</option>
                  <option value="field_user">Field User</option>
                  <option value="country_manager">Country Manager</option>
                  <option value="system_admin">System Admin</option>
                </select>
              </div>

              {(formData.role === "country_manager" || formData.role === "system_admin") && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontWeight: "500",
                    }}
                  >
                    Country
                  </label>
                  <div style={{ fontSize: "13px", color: "#4a5568", padding: "8px 0" }}>
                    {formData.role === "system_admin" ? "System Admin" : "Country Manager"} accounts can be
                    assigned to multiple countries — save this form first, then use the{" "}
                    <strong>Countries</strong> page (admin menu) to assign countries to this user.
                  </div>
                </div>
              )}

              {(formData.role === "supporter" || formData.role === "field_user") && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontWeight: "500",
                    }}
                  >
                    Country *
                  </label>
                  <select
                    name="homeCountryId"
                    value={formData.homeCountryId}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {countriesError && (
                    <div style={{ fontSize: "12px", color: "#c05621", marginTop: "5px" }}>
                      Could not load countries — try refreshing.
                    </div>
                  )}
                  {!countriesError && countries.length === 0 && (
                    <div style={{ fontSize: "12px", color: "#4a5568", marginTop: "5px" }}>
                      No countries set up yet. Visit Countries (admin menu) to add one.
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  style={{ marginRight: "8px" }}
                />
                <label>Active</label>
              </div>
            </div>

            {error && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  backgroundColor: "#fed7d7",
                  color: "#c53030",
                  borderRadius: "4px",
                }}
              >
                {error}
              </div>
            )}

            {success && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  backgroundColor: "#c6f6d5",
                  color: "#276749",
                  borderRadius: "4px",
                }}
              >
                {success}
              </div>
            )}

            <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
              <button
                type="submit"
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#48bb78",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {editingUser ? "Update User" : "Create User"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#718096",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Action Bar */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <button
          onClick={() => setShowAddForm(true)}
          style={{
            padding: "10px 20px",
            backgroundColor: "#4299e1",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          + Add New User
        </button>

        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          style={{
            padding: "8px 12px",
            border: "1px solid #ddd",
            borderRadius: "4px",
            width: "300px",
          }}
        />
      </div>

      {/* Users Table */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                backgroundColor: "#f7fafc",
                borderBottom: "2px solid #e2e8f0",
              }}
            >
              <th style={{ padding: "12px", textAlign: "left" }}>Name</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Username</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Email</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Role</th>
              <th style={{ padding: "12px", textAlign: "left" }}>
                Country
              </th>
              <th style={{ padding: "12px", textAlign: "left" }}>Status</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#4a5568",
                  }}
                >
                  {searchTerm
                    ? "No users found matching your search"
                    : "No users found"}
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "12px" }}>
                    {u.full_name}
                  </td>
                  <td style={{ padding: "12px" }}>{u.username}</td>
                  <td style={{ padding: "12px" }}>{u.email}</td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        backgroundColor:
                          isAdminRole(u.role) ? "#805ad5" : "#4299e1",
                        color: "white",
                      }}
                    >
                      {u.role.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>{countries.find((c) => c.id === u.home_country_id)?.name || "-"}</td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor: u.active ? "#c6f6d5" : "#fed7d7",
                        color: u.active ? "#276749" : "#c53030",
                      }}
                    >
                      {u.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <button
                      onClick={() => handleEdit(u)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#4299e1",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        marginRight: "5px",
                        fontSize: "14px",
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(u.id, u.active)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: u.active ? "#ed8936" : "#48bb78",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        marginRight: "5px",
                        fontSize: "14px",
                      }}
                    >
                      {u.active ? "Deactivate" : "Activate"}
                    </button>
                    {u.username !== user.username && (
                      <button
                        onClick={() => handleDelete(u.id, u.username)}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#f56565",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: "20px",
          color: "white",
          fontSize: "14px",
          textAlign: "center",
        }}
      >
        Total Users: {filteredUsers.length} | Admins:{" "}
        {filteredUsers.filter((u) => isAdminRole(u.role)).length} | Supporters:{" "}
        {filteredUsers.filter((u) => u.role === "supporter").length}
      </div>
    </div>
  );
}

export default UserManagement;
